<template>
  <!--
    A shell inside a shell renders nothing but its content: the app already mounted the chrome
    above the routed view, see `Portal.vue`.
  -->
  <div v-if="isNestedInAppShell" class="app-shell-nested">
    <slot />
  </div>

  <v-app v-else>
    <v-system-bar
      v-if="updateAvailable || !isProd"
      :color="updateAvailable ? 'info' : 'warning'"
      id="systembar"
      fixed
      style="z-index: 20"
    >
      <template v-if="updateAvailable">
        <v-icon>mdi-update</v-icon>
        <span>{{ $t("navigation.systemBar.updateAvailable") }}</span>
        <v-spacer></v-spacer>
        <v-btn x-small text @click="applyUpdate" dark>
          {{ $t("navigation.systemBar.updateAction") }}
        </v-btn>
      </template>
      <template v-else>
        <v-icon>mdi-alert-outline</v-icon>
        <v-tooltip bottom>
          <template v-slot:activator="{ on }">
            <span v-on="on">Development Environment</span>
          </template>
          <span>Development environment is strictly meant for testing purposes and is not bound to any SLA</span>
        </v-tooltip>
        <v-spacer></v-spacer>
      </template>
    </v-system-bar>

    <the-navigation-base-v2 v-if="!isHeadless && isNavV2" class="no-print"></the-navigation-base-v2>
    <the-navigation-base v-else-if="!isHeadless" class="no-print"></the-navigation-base>

    <v-app-bar
      v-if="!isHeadless"
      app
      flat
      :color="$vuetify.theme.dark ? backgroundDarkColor : backgroundColor"
      class="no-print"
    >
      <v-app-bar-nav-icon
        data-test-hamburger
        @click.stop="toggleDrawer"
        :color="$vuetify.theme.dark ? 'white' : 'grey darken-4'"
        aria-label="Menu"
      ></v-app-bar-nav-icon>

      <quick-link-menu v-if="isAuthenticated && isQuicklinks && userLoaded" @click="$refs.commandPalette.open()" />

      <v-spacer />

      <v-toolbar-title>
        <!-- `eager` and no transition, so that the logo does not fade in again on every navigation -->
        <v-img :src="tryHeaderLogo" contain style="max-height: 40px" eager :transition="false" @click="logoClick" />
      </v-toolbar-title>
      <template v-if="isAuthenticated">
        <v-spacer />

        <command-palette ref="commandPalette"></command-palette>
        <partner-report-dialog ref="partnerReportDialog" />

        <div class="app-bar-actions" data-test-app-bar-actions>
          <v-btn
            icon
            :aria-label="$t('nav.PartnerReportCreate.title')"
            @click="$refs.partnerReportDialog.open()"
            v-if="isNavV2 && canCreateReport && isBroker"
          >
            <v-icon>mdi-plus</v-icon>
          </v-btn>

          <v-btn icon @click="goToInbox" v-if="isInboxEnabled && readMessage && userLoaded">
            <v-badge :value="!!unreadMessagesCount" :content="`${unreadMessagesCount}`" overlap>
              <v-icon>mdi-email-outline</v-icon>
            </v-badge>
          </v-btn>

          <activity-inbox-button v-if="isActivityInboxEnabled && userLoaded" @click="toggleActivityInbox" />
        </div>

        <profile-menu :isEndUser="isEndUser" />
      </template>
      <template v-else-if="showLoginButton">
        <v-spacer />
        <v-btn :color="color" v-if="!isAuthenticated" @click="goToLogin" icon aria-label="Login">
          <v-icon>mdi-account</v-icon>
        </v-btn>
      </template>
      <template v-else>
        <v-spacer class="ml-12" />
      </template>

      <v-progress-linear v-if="progress" :value="progress" absolute bottom :color="color" aria-label="Progress">
      </v-progress-linear>
    </v-app-bar>

    <activity-inbox-card v-if="isActivityInboxOpen" class="no-print" @close="isActivityInboxOpen = false" />

    <!-- Sizes your content based upon application components -->
    <v-main>
      <slot />
    </v-main>
  </v-app>
</template>

<script lang="ts">
import ProfileMenu from "@/components/profile/ProfileMenu.vue";
import TheNavigationBase from "@/layouts/TheNavigationBase.vue";
import { AssetRepository } from "@/lib/AssetRepository";
import { AppContextEnum } from "@/lib/enum/appContext.enum";
import { getDefaultPartnerColor } from "@/lib/getDefaultPartnerColor";
import { unreadInboxMessageCount } from "@/lib/utility/inbox-unread";
import PermissionMixin from "@/mixins/PermissionMixin.vue";
import { ConfigModule } from "@/store/modules/config";
import { FeatureModule } from "@/store/modules/feature.store";
import { UserModule } from "@/store/modules/me-user.store";
import { NavigationModule } from "@/store/modules/navigation.store";
import { PartnerModule } from "@/store/modules/partner";
import { ReportModule } from "@/store/modules/report.store";
import { UiModule } from "@/store/modules/ui.store";
import { Component, Inject, Prop } from "vue-property-decorator";
import PartnerReportDialog from "@/components/partner/PartnerReportCreateDialog.vue";
import { PartnerTypeEnum } from "@/store/enum/partner/partner.type.enum";
import { applyUpdate as applyServiceWorkerUpdate } from "@/registerServiceWorker";
import ActivityInboxButton from "@/components/activity/ActivityInboxButton.vue";
import CommandPalette from "./navigation/CommandPalette.vue";
import QuickLinkMenu from "./navigation/QuickLinkMenu.vue";
import TheNavigationBaseV2 from "./TheNavigationBaseV2.vue";

/**
 * Key an app shell provides so that a nested one knows it is not the one rendering the chrome
 */
export const APP_SHELL_PROVIDE_KEY = "isAppShellProvided";

/**
 * The chrome around a view: the navigation drawer, the app bar and the system bar.
 *
 * Rendered once above the routed view - see `Portal.vue` - so that navigating does not destroy
 * and rebuild the navigation. `TheLayoutBase` renders one for the apps that do not hoist it yet.
 *
 * A shell nested below another shell renders only its content, so a view that brings its own
 * layout keeps working when the app mounts the shell above it.
 */
@Component({
  components: {
    TheNavigationBase,
    TheNavigationBaseV2,
    ProfileMenu,
    QuickLinkMenu,
    CommandPalette,
    PartnerReportDialog,
    ActivityInboxButton,
    /**
     * Loaded on demand. The card reaches the reference system, which reaches the portal layout and
     * so this shell again: importing it eagerly closes that cycle and leaves the modules in it
     * undefined at evaluation time. It only renders once the inbox is opened, so its chunk is not
     * wanted up front either.
     */
    ActivityInboxCard: () => import("@/components/activity/ActivityInboxCard.vue")
  },
  provide: { [APP_SHELL_PROVIDE_KEY]: true }
})
export default class TheAppShell extends PermissionMixin {
  @Prop({ default: "white" })
  backgroundColor!: string;

  @Prop({ default: "grey darken-3" })
  backgroundDarkColor!: string;

  @Prop({ default: false })
  showLoginButton!: boolean;

  @Prop({ default: 0 })
  progress!: number;

  @Prop({ default: false })
  isEndUser!: boolean;

  /**
   * Whether an app shell is already rendering the chrome above this one
   */
  @Inject({ from: APP_SHELL_PROVIDE_KEY, default: false })
  isNestedInAppShell!: boolean;

  /**
   * Whether the side card with the activities the user received is displayed
   */
  isActivityInboxOpen = false;

  get isNavV2() {
    return ConfigModule.appContext === AppContextEnum.REPORT_PORTAL;
  }

  get isBroker() {
    return this.partner.partnerType === PartnerTypeEnum.BROKER;
  }

  get isProd() {
    return ConfigModule.isProd;
  }

  get updateAvailable() {
    return ConfigModule.updateAvailable;
  }

  get isHeadless() {
    return ConfigModule.headless;
  }

  get isAuthenticated() {
    return UserModule.isAuthenticated;
  }

  get userLoaded() {
    return UserModule.user._id;
  }

  get isQuicklinks(): boolean {
    return NavigationModule.navigationConfiguration.isQuicklinks;
  }

  get partner() {
    return PartnerModule.partner ?? ReportModule.partner;
  }

  get color() {
    return getDefaultPartnerColor();
  }

  get isInboxEnabled() {
    return (
      [AppContextEnum.THG_PORTAL, AppContextEnum.REPORT_PORTAL].includes(ConfigModule.appContext) &&
      FeatureModule.isInboxEnabled
    );
  }

  /**
   * The activity log is only reachable in the portals, and only for a user that may read activities.
   * Behind a feature flag while the inbox is being rolled out.
   */
  get isActivityInboxEnabled() {
    return (
      FeatureModule.isActivityInboxEnabled &&
      [AppContextEnum.THG_PORTAL, AppContextEnum.REPORT_PORTAL].includes(ConfigModule.appContext) &&
      this.canReadActivity
    );
  }

  get unreadMessagesCount() {
    return unreadInboxMessageCount();
  }

  /**
   * The logo of the app bar by priority: the one of the view, the branding of the partner and
   * finally the logo of the app.
   *
   * The view sets its logo on `UiStore`, because the shell is not rebuilt with the view.
   */
  get tryHeaderLogo() {
    if (UiModule.headerLogo) {
      return UiModule.headerLogo;
    }

    if (this.partner.settings?.reportSettings?.logoUrl) {
      return this.partner.settings?.reportSettings?.logoUrl;
    }

    if (this.partner.settings?.logoUrl) {
      return this.partner.settings?.logoUrl;
    }

    return this.logoUrl;
  }

  get logoUrl() {
    return AssetRepository.getAsset(ConfigModule.darkMode, ConfigModule._portalLogo);
  }

  applyUpdate() {
    applyServiceWorkerUpdate();
  }

  toggleDrawer() {
    UiModule.setDrawer(!UiModule.drawer);
  }

  goToLogin() {
    this.$router.push({ path: "/login" });
  }

  toggleActivityInbox() {
    this.isActivityInboxOpen = !this.isActivityInboxOpen;
  }

  goToInbox() {
    this.$router.push({ name: "PartnerMessageInboxList", params: { partnerId: this.partner.id } });
  }

  logoClick() {
    this.$emit("logoClick", true);
    this.$log.info("Logo clicked");
  }
}
</script>

<style scoped>
/**
 * The shell needs a root element, but its box must not end up between `v-main` and the view: a
 * view sized in percent - `fill-height` on a map, `height: 100%` on an embed - resolves its height
 * against its parent, and an `auto` one makes it collapse to nothing. `display: contents` drops the
 * box from the layout tree, so the content lays out as a direct child of `v-main` again.
 */
.app-shell-nested {
  display: contents;
}

/**
 * These are independent actions, not mutually exclusive options. Keep the compact spacing of the
 * former button toggle without registering the buttons as selectable items.
 */
.app-bar-actions {
  align-items: center;
  display: inline-flex;
  max-width: 100%;
}
</style>

<style>
.v-toolbar__content {
  padding: 4px 28px !important;
}
@media print {
  .no-print,
  .no-print * {
    display: none !important;
  }
  .print-at-pagestart {
    margin-top: -40px;
  }
}
</style>
